import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LOGO_URL, NAV_LINKS } from "@/lib/constants";
import { useAppHandoff } from "@/components/layout/AppHandoff";

export default function Header() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const { openApp } = useAppHandoff();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); }, [location.pathname]);

    return (
        <motion.header
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "backdrop-blur-xl bg-paper/90 border-b border-navy/10"
                    : "bg-transparent"
            }`}
            data-testid="site-header"
        >
            <div className="max-w-[1500px] mx-auto px-6 lg:px-10 h-[88px] flex items-center justify-between">
                <Link to="/" className="flex items-center group" data-testid="nav-link-home">
                    <img
                        src={LOGO_URL}
                        alt="Carbonis"
                        className="h-14 lg:h-[60px] w-auto"
                    />
                </Link>

                <nav className="hidden lg:flex items-center gap-10">
                    {NAV_LINKS.map((link) => (
                        link.external ? (
                            <a
                                key={link.path}
                                href={link.path}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => {
                                    e.preventDefault();
                                    openApp(link.path);
                                }}
                                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                                className="group text-[11px] tracking-[0.22em] uppercase font-normal text-navy/60 hover:text-gold transition-colors duration-300"
                            >
                                <span className="relative inline-flex items-center gap-1.5 pb-1">
                                    {link.name}
                                    <ArrowUpRight
                                        size={11}
                                        strokeWidth={1.5}
                                        className="translate-y-[-0.5px] transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-1"
                                    />
                                </span>
                            </a>
                        ) : (
                            <NavLink
                                key={link.path}
                                to={link.path}
                                data-testid={`nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                                className={({ isActive }) =>
                                    `relative text-[11px] tracking-[0.22em] uppercase font-normal transition-colors duration-300 ${
                                        isActive ? "text-navy" : "text-navy/60 hover:text-navy"
                                    }`
                                }
                            >
                                {({ isActive }) => (
                                    <span className="relative inline-block pb-1">
                                        {link.name}
                                        <span
                                            className={`absolute left-0 right-0 -bottom-0.5 h-px bg-gold transition-transform duration-500 origin-left ${
                                                isActive ? "scale-x-100" : "scale-x-0"
                                            }`}
                                        />
                                    </span>
                                )}
                            </NavLink>
                        )
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-4">
                    <Link to="/contact" className="btn-institution" data-testid="nav-cta-demo">
                        Request Briefing <ArrowUpRight size={14} strokeWidth={1.5} />
                    </Link>
                </div>

                <button
                    className="lg:hidden text-navy p-2"
                    onClick={() => setOpen((v) => !v)}
                    aria-label="Menu"
                    data-testid="mobile-menu-toggle"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="lg:hidden overflow-hidden bg-paper/95 backdrop-blur-xl border-t border-navy/10"
                    >
                        <div className="px-6 py-8 flex flex-col gap-5">
                            {NAV_LINKS.map((link) => (
                                link.external ? (
                                    <a
                                        key={link.path}
                                        href={link.path}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            openApp(link.path);
                                        }}
                                        data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                                        className="inline-flex items-center gap-2 text-2xl font-serif text-navy hover:text-gold transition-colors"
                                    >
                                        {link.name}
                                        <ArrowUpRight size={18} strokeWidth={1.25} />
                                    </a>
                                ) : (
                                    <NavLink
                                        key={link.path}
                                        to={link.path}
                                        data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                                        className={({ isActive }) =>
                                            `text-2xl font-serif ${isActive ? "text-gold" : "text-navy"}`
                                        }
                                    >
                                        {link.name}
                                    </NavLink>
                                )
                            ))}
                            <Link to="/contact" className="btn-institution mt-4 self-start">
                                Request Briefing <ArrowUpRight size={14} />
                            </Link>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
}
